// src/views/admin_sys/SalaryPrimManagement/admin_salary_prim_management.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { api, usersAPI, staffSalaryAPI, transactionsAPI } from 'services/api'

// ─── HELPERS — birebir aynı ───────────────────────────────────────────────────
const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.split(' ')
    return `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ''}`.toUpperCase()
}

const formatCurrency = (val) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val)

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('tr-TR').format(date)
}

// getTransactionLabel — birebir aynı (kategori bazlı + fallback metin kontrolü)
const getTransactionLabel = (tx) => {
    if (tx.category === 'salary_expense')    return { text: 'Maaş Ödemesi',  color: 'info' }
    if (tx.category === 'commission_expense')return { text: 'Prim Ödemesi',  color: 'warning' }
    if (tx.category === 'manual_expense')    return { text: 'Diğer Gider',   color: 'secondary' }
    if (tx.category === 'manual_income')     return { text: 'Ek Gelir',      color: 'success' }
    if (tx.category === 'service_revenue')   return { text: 'Hizmet Geliri', color: 'primary' }

    const fullText = (tx.title + ' ' + tx.description + ' ' + (tx.category || '')).toLowerCase()
    if (fullText.includes('maaş')) return { text: 'Maaş Ödemesi', color: 'info' }
    if (fullText.includes('prim')) return { text: 'Prim Ödemesi', color: 'warning' }
    if (fullText.includes('avans')) return { text: 'Avans',       color: 'danger' }
    return { text: 'Genel İşlem', color: 'neutral' }
}

// ─── AVATAR COLORS ────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    ['#7c5cfc','#f0eeff'],['#12b886','#e6faf4'],['#f59e0b','#fef3cd'],
    ['#3b82f6','#dbeafe'],['#ec4899','#fce7f3'],['#8b5cf6','#ede9fe'],
    ['#06b6d4','#cffafe'],['#ef4444','#fee2e2'],
]
const avatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0]
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]
}

// ─── TX BADGE COLOR MAP ───────────────────────────────────────────────────────
const TX_BADGE_STYLES = {
    info:      { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6' },
    warning:   { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
    secondary: { bg: 'rgba(107,114,128,0.12)', color: '#6b7280' },
    success:   { bg: 'rgba(18,184,134,0.12)',  color: '#12b886' },
    primary:   { bg: 'rgba(124,92,252,0.12)',  color: '#7c5cfc' },
    danger:    { bg: 'rgba(250,82,82,0.12)',   color: '#fa5252' },
    neutral:   { bg: 'rgba(156,163,175,0.12)', color: '#9ca3af' },
}

// ─── SVG ICONS ────────────────────────────────────────────────────────────────
const XIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
)
const SpinnerIcon = ({ size=15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{animation:'spm-spin 0.8s linear infinite',display:'inline-block'}}>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.2"/>
        <path d="M21 12a9 9 0 00-9-9"/>
    </svg>
)
const SearchIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
)
const WalletIcon = ({ size=20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
        <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z"/>
    </svg>
)
const SettingsIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
)
const HistoryIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="1 4 1 10 7 10"/>
        <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
        <polyline points="12 7 12 12 15 15"/>
    </svg>
)
const MoneyIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
)
const CreditCardIcon = ({ size=14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
)
const ArrowRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
)
const SaveIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
        <polyline points="17 21 17 13 7 13 7 21"/>
        <polyline points="7 3 7 8 15 8"/>
    </svg>
)
const PlusIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
)
const TrashIcon = ({ size=13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
)
const InfoIcon = ({ size=20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
)
const BookIcon = ({ size=15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>
)
const LightbulbIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/>
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
)
const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
)
const ChevronUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="18 15 12 9 6 15"/>
    </svg>
)
const WarnIcon = ({ size=16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .spm-page {
      font-family: 'Plus Jakarta Sans', sans-serif;
      background: transparent;          /* NO wrapper background */

      --spm-accent:    #7c5cfc;
      --spm-accent-l:  rgba(124,92,252,0.12);
      --spm-accent-h:  #6b4edb;
      --spm-success:   #12b886;
      --spm-success-l: rgba(18,184,134,0.12);
      --spm-error:     #fa5252;
      --spm-error-l:   rgba(250,82,82,0.12);
      --spm-warn:      #f59e0b;
      --spm-warn-l:    rgba(245,158,11,0.12);
      --spm-info:      #3b82f6;
      --spm-info-l:    rgba(59,130,246,0.12);

      /* light */
      --spm-card:      #ffffff;
      --spm-card2:     #f4f2ff;
      --spm-card3:     #f9f8ff;
      --spm-border:    rgba(124,92,252,0.12);
      --spm-border2:   rgba(0,0,0,0.07);
      --spm-text1:     #1a1a2e;
      --spm-text2:     #6b7280;
      --spm-text3:     #9ca3af;
      --spm-shadow:    0 4px 24px rgba(124,92,252,0.07);
      --spm-shadow-lg: 0 12px 40px rgba(124,92,252,0.13);
      --spm-skel1:     rgba(124,92,252,0.08);
      --spm-skel2:     rgba(124,92,252,0.16);
      --spm-thead:     #f9f8ff;
    }

    /* ── DARK MODE ── */
    [data-coreui-theme="dark"] .spm-page,
    [data-bs-theme="dark"] .spm-page,
    .dark .spm-page {
      --spm-card:      #1a1928;
      --spm-card2:     #1f1e2e;
      --spm-card3:     #16152a;
      --spm-border:    rgba(124,92,252,0.2);
      --spm-border2:   rgba(255,255,255,0.06);
      --spm-text1:     #f0eeff;
      --spm-text2:     #a09bc0;
      --spm-text3:     #6b6785;
      --spm-shadow:    0 4px 24px rgba(0,0,0,0.35);
      --spm-shadow-lg: 0 12px 40px rgba(0,0,0,0.5);
      --spm-skel1:     rgba(124,92,252,0.08);
      --spm-skel2:     rgba(124,92,252,0.18);
      --spm-thead:     #16152a;
    }

    /* ── ANIMATIONS ── */
    @keyframes spm-spin      { to { transform: rotate(360deg); } }
    @keyframes spm-shimmer   { 0%{background-position:200% 0}100%{background-position:-200% 0} }
    @keyframes spm-fade-up   { from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none} }
    @keyframes spm-modal-in  { from{opacity:0;transform:translateY(-14px) scale(0.97)}to{opacity:1;transform:none} }
    @keyframes spm-model-in  { from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none} }

    .spm-fade { animation: spm-fade-up 0.3s ease both; }

    /* ── SKELETON ── */
    .spm-skel {
      background: linear-gradient(90deg,var(--spm-skel1) 25%,var(--spm-skel2) 50%,var(--spm-skel1) 75%);
      background-size: 200% 100%;
      animation: spm-shimmer 1.5s infinite;
      border-radius: 8px;
    }

    /* ── HEADER CARD ── */
    .spm-header-card {
      background: var(--spm-card); border-radius: 20px;
      border: 1px solid var(--spm-border); box-shadow: var(--spm-shadow);
      padding: 20px 24px; margin-bottom: 20px;
      display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 14px;
    }
    .spm-header-left { display: flex; align-items: center; gap: 14px; }
    .spm-header-icon {
      width: 48px; height: 48px; border-radius: 14px;
      background: var(--spm-accent-l); color: var(--spm-accent);
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .spm-header-title { font-size: 1.15rem; font-weight: 800; color: var(--spm-text1); margin: 0; }
    .spm-header-sub   { font-size: 0.78rem; color: var(--spm-text3); margin: 2px 0 0; }

    /* ── SEARCH ── */
    .spm-search-wrap { position: relative; }
    .spm-search-wrap svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--spm-text3); pointer-events: none; }
    .spm-search {
      padding: 9px 12px 9px 36px;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      border-radius: 11px; color: var(--spm-text1);
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.82rem;
      outline: none; transition: border-color 0.2s; min-width: 220px;
    }
    .spm-search:focus { border-color: var(--spm-accent); }
    .spm-search::placeholder { color: var(--spm-text3); }

    /* ── STAFF GRID ── */
    .spm-staff-grid {
      display: grid; gap: 14px;
      grid-template-columns: 1fr;
    }
    @media(min-width:640px)  { .spm-staff-grid { grid-template-columns: repeat(2,1fr); } }
    @media(min-width:1100px) { .spm-staff-grid { grid-template-columns: repeat(3,1fr); } }

    /* ── STAFF CARD ── */
    .spm-staff-card {
      background: var(--spm-card); border-radius: 20px;
      border: 2px solid var(--spm-border); box-shadow: var(--spm-shadow);
      overflow: hidden; display: flex; flex-direction: column;
      transition: all 0.25s ease;
      animation: spm-fade-up 0.3s ease both;
    }
    .spm-staff-card:hover {
      border-color: var(--spm-accent);
      transform: translateY(-5px);
      box-shadow: 0 12px 36px rgba(124,92,252,0.18);
    }

    /* card header */
    .spm-sc-header {
      padding: 18px 20px 14px;
      display: flex; align-items: center; gap: 14px;
      border-bottom: 1px solid var(--spm-border2);
    }
    .spm-avatar {
      width: 58px; height: 58px; border-radius: 50%; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.1rem; font-weight: 800;
      border: 2.5px solid var(--spm-accent); position: relative;
    }
    .spm-avatar img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
    .spm-avatar-status {
      position: absolute; bottom: 1px; right: 1px;
      width: 13px; height: 13px; border-radius: 50%;
      border: 2px solid var(--spm-card);
    }
    .spm-staff-name { font-size: 0.95rem; font-weight: 800; color: var(--spm-text1); margin: 0 0 4px; }
    .spm-staff-tag  {
      display: inline-block; font-size: 0.65rem; font-weight: 700;
      padding: 2px 9px; border-radius: 20px;
      background: var(--spm-card2); border: 1px solid var(--spm-border2);
      color: var(--spm-text3);
    }

    /* card mid — action buttons */
    .spm-sc-mid { padding: 14px 20px; display: flex; gap: 8px; border-bottom: 1px solid var(--spm-border2); }

    /* card bottom — payment zone */
    .spm-sc-bottom {
      padding: 14px 20px 18px; margin-top: auto;
      display: flex; flex-direction: column; gap: 8px;
    }
    .spm-pay-zone {
      background: var(--spm-card2); border: 1px solid var(--spm-border);
      border-radius: 14px; padding: 12px 14px;
      display: flex; flex-direction: column; gap: 6px;
    }
    .spm-pay-btn {
      display: flex; align-items: center; gap: 9px;
      padding: 9px 12px; border-radius: 10px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.82rem; font-weight: 700;
      background: none; border: none; cursor: pointer;
      color: var(--spm-text1); transition: all 0.18s; text-align: left;
    }
    .spm-pay-btn:hover { background: rgba(124,92,252,0.06); }
    .spm-pay-divider { height: 1px; background: var(--spm-border2); }

    /* ── ICON BUTTONS ── */
    .spm-icon-btn {
      flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
      padding: 8px 12px; border-radius: 10px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.75rem; font-weight: 700;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      color: var(--spm-text2); cursor: pointer; transition: all 0.18s;
    }
    .spm-icon-btn.info:hover  { border-color: var(--spm-info);    color: var(--spm-info);    background: var(--spm-info-l); }
    .spm-icon-btn.warn:hover  { border-color: var(--spm-warn);    color: var(--spm-warn);    background: var(--spm-warn-l); }

    /* ── BUTTONS ── */
    .spm-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 7px;
      padding: 10px 20px; border-radius: 12px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem; font-weight: 700;
      cursor: pointer; transition: all 0.18s; border: none; white-space: nowrap;
    }
    .spm-btn-primary { background: var(--spm-accent); color: #fff; }
    .spm-btn-primary:hover:not(:disabled) { background: var(--spm-accent-h); box-shadow: 0 4px 14px rgba(124,92,252,0.3); }
    .spm-btn-info    { background: var(--spm-info);    color: #fff; }
    .spm-btn-info:hover:not(:disabled)    { background: #2563eb; }
    .spm-btn-warn    { background: var(--spm-warn);    color: #fff; }
    .spm-btn-warn:hover:not(:disabled)    { background: #d97706; }
    .spm-btn-ghost   { background: var(--spm-card2); color: var(--spm-text2); border: 1.5px solid var(--spm-border); }
    .spm-btn-ghost:hover:not(:disabled)   { border-color: var(--spm-accent); color: var(--spm-accent); }
    .spm-btn-danger-ghost { background: none; color: var(--spm-error); border: 1.5px solid rgba(250,82,82,0.3); border-radius: 8px; padding: 4px 8px; cursor: pointer; transition: all 0.15s; font-family: 'Plus Jakarta Sans', sans-serif; }
    .spm-btn-danger-ghost:hover { background: var(--spm-error-l); border-color: var(--spm-error); }
    .spm-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ── ALERT / ERROR ── */
    .spm-alert {
      display: flex; align-items: center; gap: 10px;
      padding: 12px 16px; border-radius: 14px;
      font-size: 0.85rem; font-weight: 600;
      margin-bottom: 16px; animation: spm-fade-up 0.25s ease;
      box-shadow: var(--spm-shadow);
    }
    .spm-alert-danger { background: var(--spm-error-l); color: var(--spm-error); border: 1px solid rgba(250,82,82,0.25); }
    .spm-alert-close  { background: none; border: none; cursor: pointer; color: inherit; opacity: 0.6; padding: 0; margin-left: auto; }
    .spm-alert-close:hover { opacity: 1; }

    /* ── FULL LOADER ── */
    .spm-loader {
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 64px 0; gap: 14px;
    }
    .spm-spinner {
      width: 48px; height: 48px; border-radius: 50%;
      border: 3px solid var(--spm-accent-l); border-top-color: var(--spm-accent);
      animation: spm-spin 0.8s linear infinite;
    }

    /* ── MODAL ── */
    .spm-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
      z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 16px;
    }
    .spm-modal {
      background: var(--spm-card); border-radius: 20px;
      border: 1px solid var(--spm-border); box-shadow: var(--spm-shadow-lg);
      width: 100%; max-width: 520px; max-height: 90vh;
      display: flex; flex-direction: column;
      animation: spm-modal-in 0.2s ease both;
    }
    .spm-modal-lg { max-width: 680px; }
    .spm-modal-header {
      padding: 18px 22px; border-bottom: 1px solid var(--spm-border2);
      display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
    }
    .spm-modal-title { font-size: 1rem; font-weight: 800; color: var(--spm-text1); }
    .spm-modal-close {
      width: 30px; height: 30px; border-radius: 8px;
      background: var(--spm-card2); border: none; cursor: pointer;
      color: var(--spm-text3); display: flex; align-items: center; justify-content: center;
      transition: all 0.15s;
    }
    .spm-modal-close:hover { background: var(--spm-error-l); color: var(--spm-error); }
    .spm-modal-body   { padding: 22px; overflow-y: auto; flex: 1; }
    .spm-modal-footer { padding: 14px 22px; border-top: 1px solid var(--spm-border2); display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

    /* ── INFO BANNER (settings modal) ── */
    .spm-info-banner {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; margin-bottom: 20px;
      background: var(--spm-accent-l); border: 1px solid var(--spm-border);
      border-radius: 12px;
    }
    .spm-info-banner-icon { color: var(--spm-accent); flex-shrink: 0; }
    .spm-info-banner-name { font-size: 0.95rem; font-weight: 800; color: var(--spm-text1); margin: 0; }
    .spm-info-banner-sub  { font-size: 0.72rem; color: var(--spm-text3); margin: 2px 0 0; }

    /* ── FORM ELEMENTS ── */
    .spm-form-label { display: block; margin-bottom: 7px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.6px; color: var(--spm-text3); text-transform: uppercase; }
    .spm-form-mb { margin-bottom: 16px; }
    .spm-input-group { display: flex; }
    .spm-input-prefix {
      padding: 10px 12px; background: var(--spm-card2);
      border: 1.5px solid var(--spm-border); border-right: none;
      border-radius: 11px 0 0 11px; color: var(--spm-text3);
      font-size: 0.9rem; font-weight: 700; display: flex; align-items: center;
    }
    .spm-input-suffix {
      padding: 10px 12px; background: var(--spm-card2);
      border: 1.5px solid var(--spm-border); border-left: none;
      border-radius: 0 11px 11px 0; color: var(--spm-text3);
      font-size: 0.82rem; font-weight: 700; display: flex; align-items: center;
    }
    .spm-input {
      flex: 1; padding: 10px 13px;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      color: var(--spm-text1); font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.9rem; font-weight: 700; outline: none;
      transition: border-color 0.2s; box-sizing: border-box;
    }
    .spm-input.solo { border-radius: 11px; }
    .spm-input.prefix-r { border-radius: 0 11px 11px 0; }
    .spm-input.suffix-l { border-radius: 11px 0 0 11px; }
    .spm-input.large { font-size: 1.1rem; text-align: center; }
    .spm-input:focus { border-color: var(--spm-accent); }
    .spm-input::placeholder { color: var(--spm-text3); font-size: 0.85rem; font-weight: 400; }
    .spm-textarea {
      width: 100%; padding: 10px 13px;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      border-radius: 11px; color: var(--spm-text1);
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem;
      resize: none; outline: none; box-sizing: border-box; transition: border-color 0.2s;
    }
    .spm-textarea:focus { border-color: var(--spm-accent); }
    .spm-textarea::placeholder { color: var(--spm-text3); }

    /* ── CUSTOM SELECT ── */
    .spm-select {
      width: 100%; padding: 10px 30px 10px 12px;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      border-radius: 11px; color: var(--spm-text1);
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem; font-weight: 600;
      outline: none; cursor: pointer; transition: border-color 0.2s;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round' fill='none'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 11px center;
    }
    .spm-select:focus { border-color: var(--spm-accent); }

    /* ── TOGGLE SWITCH ── */
    .spm-toggle-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: var(--spm-card2); border: 1.5px solid var(--spm-border); border-radius: 11px; }
    .spm-toggle { position: relative; display: inline-block; flex-shrink: 0; }
    .spm-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
    .spm-toggle-track { display: block; width: 38px; height: 21px; background: var(--spm-border); border-radius: 20px; cursor: pointer; transition: background 0.2s; }
    .spm-toggle input:checked + .spm-toggle-track { background: var(--spm-accent); }
    .spm-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 15px; height: 15px; border-radius: 50%; background: #fff; transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.2); }
    .spm-toggle input:checked ~ .spm-toggle-track .spm-toggle-thumb { transform: translateX(17px); }
    .spm-toggle-label { font-size: 0.8rem; font-weight: 600; color: var(--spm-text2); }

    /* ── MODEL SETTINGS BOX ── */
    .spm-model-box {
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      border-radius: 14px; padding: 16px; margin-top: 14px;
      animation: spm-model-in 0.25s ease both;
    }
    .spm-model-box-title {
      font-size: 0.78rem; font-weight: 800; letter-spacing: 0.4px;
      margin: 0 0 14px; padding-bottom: 10px;
      border-bottom: 1px solid var(--spm-border2);
      display: flex; align-items: center; justify-content: space-between;
    }
    .spm-model-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .spm-model-hint { font-size: 0.72rem; color: var(--spm-text3); margin: 8px 0 0; }
    .spm-sub-rates-box {
      background: var(--spm-card); border: 1px solid var(--spm-border2);
      border-radius: 10px; padding: 12px; margin-top: 10px;
    }
    .spm-sub-rates-label { font-size: 0.65rem; font-weight: 800; letter-spacing: 0.6px; text-transform: uppercase; color: var(--spm-text3); margin-bottom: 10px; }
    .spm-sm-label { font-size: 0.7rem; color: var(--spm-text3); font-weight: 600; margin-bottom: 5px; display: block; }
    .spm-input-sm { padding: 7px 10px; font-size: 0.82rem; }

    /* tier row */
    .spm-tier-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .spm-tier-row:last-child { margin-bottom: 0; }

    /* ── HISTORY TABLE ── */
    .spm-table-scroll { overflow-y: auto; max-height: 380px; }
    .spm-table { width: 100%; border-collapse: collapse; }
    .spm-table thead tr { background: var(--spm-thead); border-bottom: 1.5px solid var(--spm-border2); }
    .spm-table thead th { padding: 10px 14px; font-size: 0.68rem; font-weight: 700; color: var(--spm-text3); text-transform: uppercase; letter-spacing: 0.5px; white-space: nowrap; }
    .spm-table tbody tr { border-bottom: 1px solid var(--spm-border2); }
    .spm-table tbody tr:last-child { border-bottom: none; }
    .spm-table tbody tr:hover { background: var(--spm-card2); }
    .spm-table tbody td { padding: 10px 14px; font-size: 0.83rem; color: var(--spm-text1); vertical-align: middle; }
    .spm-table-amount { font-weight: 800; color: var(--spm-error); white-space: nowrap; }
    .spm-table-date   { color: var(--spm-text3); white-space: nowrap; }

    /* ── TX BADGE ── */
    .spm-tx-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 0.68rem; font-weight: 700; white-space: nowrap; }

    /* ── HISTORY EMPTY ── */
    .spm-history-empty { display: flex; align-items: center; justify-content: center; padding: 48px 24px; color: var(--spm-text3); font-size: 0.85rem; font-weight: 500; font-style: italic; }

    /* ── HISTORY LOADER ── */
    .spm-modal-spinner { display: flex; align-items: center; justify-content: center; padding: 48px; }

    /* ── ACCORDION ── */
    .spm-accordion { display: flex; flex-direction: column; gap: 8px; }
    .spm-acc-item { background: var(--spm-card2); border: 1.5px solid var(--spm-border); border-radius: 14px; overflow: hidden; }
    .spm-acc-header {
      width: 100%; display: flex; align-items: center; justify-content: space-between;
      padding: 13px 16px; background: none; border: none; cursor: pointer;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem; font-weight: 700;
      color: var(--spm-text1); text-align: left; transition: background 0.15s;
    }
    .spm-acc-header:hover { background: rgba(124,92,252,0.04); }
    .spm-acc-body { padding: 0 16px 14px; font-size: 0.82rem; color: var(--spm-text2); line-height: 1.65; animation: spm-model-in 0.2s ease; }
    .spm-acc-body ul { padding-left: 16px; margin: 8px 0 0; }
    .spm-acc-body li { margin-bottom: 4px; }

    /* ── HELP BTN ── */
    .spm-help-btn {
      display: inline-flex; align-items: center; gap: 8px;
      padding: 9px 16px; border-radius: 11px;
      background: var(--spm-card2); border: 1.5px solid var(--spm-border);
      color: var(--spm-text2); font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.8rem; font-weight: 700; cursor: pointer; transition: all 0.18s;
    }
    .spm-help-btn:hover { border-color: var(--spm-warn); color: var(--spm-warn); background: var(--spm-warn-l); }

    /* ── SKELETON CARD ── */
    .spm-skel-card { background: var(--spm-card); border-radius: 20px; border: 2px solid var(--spm-border); overflow: hidden; }
  `}</style>
)

// ─── ACCORDION ────────────────────────────────────────────────────────────────
const AccordionItem = ({ title, children, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen)
    return (
        <div className="spm-acc-item">
            <button className="spm-acc-header" onClick={() => setOpen(p => !p)}>
                <span>{title}</span>
                {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
            {open && <div className="spm-acc-body">{children}</div>}
        </div>
    )
}

// ─── MODAL ────────────────────────────────────────────────────────────────────
const Modal = ({ visible, onClose, title, children, footer, size = '' }) => {
    useEffect(() => {
        const fn = (e) => e.key === 'Escape' && onClose()
        if (visible) document.addEventListener('keydown', fn)
        return () => document.removeEventListener('keydown', fn)
    }, [visible, onClose])
    if (!visible) return null
    return (
        <div className="spm-modal-overlay" onClick={(e) => e.target.classList.contains('spm-modal-overlay') && onClose()}>
            <div className={`spm-modal ${size}`}>
                <div className="spm-modal-header">
                    <span className="spm-modal-title">{title}</span>
                    <button className="spm-modal-close" onClick={onClose}><XIcon size={14} /></button>
                </div>
                <div className="spm-modal-body">{children}</div>
                {footer && <div className="spm-modal-footer">{footer}</div>}
            </div>
        </div>
    )
}

// ─── TOGGLE SWITCH ────────────────────────────────────────────────────────────
const ToggleSwitch = ({ id, checked, onChange, label }) => (
    <div className="spm-toggle-row">
        <label className="spm-toggle" htmlFor={id}>
            <input id={id} type="checkbox" checked={checked} onChange={onChange} />
            <span className="spm-toggle-track">
        <span className="spm-toggle-thumb" />
      </span>
        </label>
        <span className="spm-toggle-label">{label}</span>
    </div>
)

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const StaffCardSkeleton = () => (
    <div className="spm-skel-card">
        <div style={{ padding: '18px 20px 14px', display: 'flex', alignItems: 'center', gap: 14, borderBottom: '1px solid var(--spm-border2)' }}>
            <div className="spm-skel" style={{ width: 58, height: 58, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
                <div className="spm-skel" style={{ width: '65%', height: 16, marginBottom: 8 }} />
                <div className="spm-skel" style={{ width: '40%', height: 12 }} />
            </div>
        </div>
        <div style={{ padding: '14px 20px', display: 'flex', gap: 8, borderBottom: '1px solid var(--spm-border2)' }}>
            <div className="spm-skel" style={{ flex: 1, height: 36, borderRadius: 10 }} />
            <div className="spm-skel" style={{ flex: 1, height: 36, borderRadius: 10 }} />
        </div>
        <div style={{ padding: '14px 20px 18px' }}>
            <div className="spm-skel" style={{ width: '100%', height: 92, borderRadius: 14 }} />
        </div>
    </div>
)

// ─── renderModelInputs — birebir aynı iş mantığı, HorizonUI form elements ────
const ModelInputs = ({ commissionModel, modelSettings, setSettingsModal }) => {
    const updateModelSetting = (key, value) => {
        setSettingsModal(prev => ({
            ...prev,
            modelSettings: { ...prev.modelSettings, [key]: value }
        }))
    }

    const updateDeepModelSetting = (parent, key, value) => {
        setSettingsModal(prev => ({
            ...prev,
            modelSettings: {
                ...prev.modelSettings,
                [parent]: {
                    ...(prev.modelSettings[parent] || {}),
                    [key]: value
                }
            }
        }))
    }

    // ── flat_rate ──────────────────────────────────────────────────────────────
    if (commissionModel === 'flat_rate') {
        return (
            <div className="spm-model-box">
                <p className="spm-model-box-title" style={{ color: 'var(--spm-info)' }}>
                    Sabit Oran Ayarları
                </p>
                <div className="spm-model-2col">
                    <div>
                        <label className="spm-sm-label">Hizmet Primi (%)</label>
                        <div className="spm-input-group">
                            <input
                                type="number"
                                className="spm-input suffix-l spm-input-sm"
                                value={modelSettings.service_rate || ''}
                                onChange={(e) => updateModelSetting('service_rate', e.target.value)}
                            />
                            <span className="spm-input-suffix">%</span>
                        </div>
                    </div>
                    <div>
                        <label className="spm-sm-label">Ürün Primi (%)</label>
                        <div className="spm-input-group">
                            <input
                                type="number"
                                className="spm-input suffix-l spm-input-sm"
                                value={modelSettings.product_rate || ''}
                                onChange={(e) => updateModelSetting('product_rate', e.target.value)}
                            />
                            <span className="spm-input-suffix">%</span>
                        </div>
                    </div>
                </div>
                <p className="spm-model-hint">Personel, yaptığı her satıştan yukarıdaki yüzdeler kadar prim alır.</p>
            </div>
        )
    }

    // ── threshold_complex ──────────────────────────────────────────────────────
    if (commissionModel === 'threshold_complex') {
        return (
            <div className="spm-model-box">
                <p className="spm-model-box-title" style={{ color: 'var(--spm-warn)' }}>
                    Eşikli & Detaylı Sistem
                </p>
                <div className="spm-model-2col" style={{ marginBottom: 12 }}>
                    <div>
                        <label className="spm-sm-label" style={{ fontWeight: 800, color: 'var(--spm-warn)' }}>Hedef Kota (Eşik TL)</label>
                        <div className="spm-input-group">
                            <span className="spm-input-prefix">₺</span>
                            <input
                                type="number"
                                className="spm-input prefix-r spm-input-sm"
                                style={{ borderColor: 'rgba(245,158,11,0.4)' }}
                                value={modelSettings.threshold || ''}
                                onChange={(e) => updateModelSetting('threshold', e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <ToggleSwitch
                            id="threshold-include"
                            checked={modelSettings.include_threshold_in_calc ?? true}
                            onChange={(e) => updateModelSetting('include_threshold_in_calc', e.target.checked)}
                            label="Kota hesaplamaya dahil edilsin mi?"
                        />
                    </div>
                </div>

                <div className="spm-sub-rates-box">
                    <p className="spm-sub-rates-label">Kota geçilince uygulanacak oranlar:</p>
                    <div className="spm-model-2col">
                        <div>
                            <label className="spm-sm-label">Hizmet (Nakit) %</label>
                            <input
                                type="number"
                                className="spm-input solo spm-input-sm"
                                value={modelSettings.rates?.service_cash || ''}
                                onChange={(e) => updateDeepModelSetting('rates', 'service_cash', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="spm-sm-label">Hizmet (Kart) %</label>
                            <input
                                type="number"
                                className="spm-input solo spm-input-sm"
                                value={modelSettings.rates?.service_card || ''}
                                onChange={(e) => updateDeepModelSetting('rates', 'service_card', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="spm-sm-label">Ürün (Nakit) %</label>
                            <input
                                type="number"
                                className="spm-input solo spm-input-sm"
                                value={modelSettings.rates?.product_cash || ''}
                                onChange={(e) => updateDeepModelSetting('rates', 'product_cash', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="spm-sm-label">Ürün (Kart) %</label>
                            <input
                                type="number"
                                className="spm-input solo spm-input-sm"
                                value={modelSettings.rates?.product_card || ''}
                                onChange={(e) => updateDeepModelSetting('rates', 'product_card', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ── tiered — birebir aynı addTier/removeTier/updateTier mantığı ────────────
    if (commissionModel === 'tiered') {
        const tiers = modelSettings.tiers || []

        const addTier = () => {
            const newTiers = [...tiers, { limit: 0, rate: 0 }]
            updateModelSetting('tiers', newTiers)
        }

        const removeTier = (index) => {
            const newTiers = tiers.filter((_, i) => i !== index)
            updateModelSetting('tiers', newTiers)
        }

        const updateTier = (index, field, val) => {
            const newTiers = [...tiers]
            newTiers[index][field] = val
            updateModelSetting('tiers', newTiers)
        }

        return (
            <div className="spm-model-box">
                <p className="spm-model-box-title" style={{ color: 'var(--spm-success)' }}>
                    <span>Kademeli (Tiered) Sistem</span>
                    <button className="spm-btn spm-btn-ghost" style={{ padding: '5px 10px', fontSize: '0.72rem', borderRadius: 8 }} onClick={addTier}>
                        <PlusIcon /> Kademe Ekle
                    </button>
                </p>

                {tiers.length === 0 && (
                    <p style={{ textAlign: 'center', color: 'var(--spm-text3)', fontSize: '0.82rem', margin: '8px 0' }}>
                        Henüz kademe eklenmemiş.
                    </p>
                )}

                {tiers.map((tier, idx) => (
                    <div className="spm-tier-row" key={idx}>
                        <div className="spm-input-group" style={{ flex: 1 }}>
                            <span className="spm-input-prefix" style={{ fontSize: '0.72rem' }}>Min ₺</span>
                            <input
                                type="number"
                                className="spm-input prefix-r spm-input-sm"
                                value={tier.limit}
                                onChange={(e) => updateTier(idx, 'limit', e.target.value)}
                            />
                        </div>
                        <span style={{ color: 'var(--spm-text3)', fontSize: '0.8rem' }}>→</span>
                        <div className="spm-input-group" style={{ flex: 1 }}>
                            <input
                                type="number"
                                className="spm-input suffix-l spm-input-sm"
                                value={tier.rate}
                                onChange={(e) => updateTier(idx, 'rate', e.target.value)}
                            />
                            <span className="spm-input-suffix">%</span>
                        </div>
                        <button className="spm-btn-danger-ghost" onClick={() => removeTier(idx)} title="Kaldır">
                            <TrashIcon size={13} />
                        </button>
                    </div>
                ))}

                <p className="spm-model-hint">Toplam ciro hangi aralığa girerse, o oran üzerinden tüm ciro hesaplanır.</p>
            </div>
        )
    }

    return null
}

// ─── STAFF CARD ───────────────────────────────────────────────────────────────
const StaffCard = ({ staff, onSettings, onHistory, onPayment, animDelay }) => {
    const fullName = `${staff.name} ${staff.surname}`
    const [bg, fg] = avatarColor(staff.name)

    return (
        <div className="spm-staff-card" style={{ animationDelay: `${animDelay}s` }}>
            {/* Header */}
            <div className="spm-sc-header">
                <div className="spm-avatar" style={{ background: bg, color: fg }}>
                    {staff.profile_picture
                        ? <img src={staff.profile_picture} alt={fullName} />
                        : getInitials(staff.name)
                    }
                    <div
                        className="spm-avatar-status"
                        style={{ background: staff.is_active ? 'var(--spm-success)' : 'var(--spm-error)' }}
                    />
                </div>
                <div>
                    <p className="spm-staff-name">{fullName}</p>
                    <span className="spm-staff-tag">{staff.tag || 'Personel'}</span>
                </div>
            </div>

            {/* Mid — Ayarlar & Geçmiş */}
            <div className="spm-sc-mid">
                <button className="spm-icon-btn info" onClick={() => onSettings(staff)}>
                    <SettingsIcon size={13} /> Ayarlar
                </button>
                <button className="spm-icon-btn warn" onClick={() => onHistory(staff)}>
                    <HistoryIcon size={13} /> Geçmiş
                </button>
            </div>

            {/* Bottom — Ödeme butonları */}
            <div className="spm-sc-bottom">
                <div className="spm-pay-zone">
                    <button className="spm-pay-btn" onClick={() => onPayment(staff, 'salary')}>
                        <MoneyIcon size={15} style={{ color: 'var(--spm-success)' }} />
                        <span>Maaş Öde</span>
                    </button>
                    <div className="spm-pay-divider" />
                    <button className="spm-pay-btn" onClick={() => onPayment(staff, 'commission')}>
                        <CreditCardIcon size={15} style={{ color: 'var(--spm-warn)' }} />
                        <span>Prim Öde</span>
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AdminSalaryPrimManagement = () => {
    // ── STATE — birebir aynı ─────────────────────────────────────────────────
    const [loading,       setLoading]       = useState(true)
    const [staffList,     setStaffList]     = useState([])
    const [filteredStaff, setFilteredStaff] = useState([])
    const [searchTerm,    setSearchTerm]    = useState('')
    const [error,         setError]         = useState(null)

    // Settings modal state — birebir aynı
    const [settingsModal, setSettingsModal] = useState({
        visible: false, staffId: null, staffName: '',
        baseSalary: 0, commissionModel: 'flat_rate', modelSettings: {}
    })
    const [savingSettings, setSavingSettings] = useState(false)

    // Payment modal state — birebir aynı
    const [paymentModal, setPaymentModal] = useState({
        visible: false, staffId: null, staffName: '',
        type: 'salary', amount: '', note: ''
    })
    const [submittingPayment, setSubmittingPayment] = useState(false)

    // History modal state — birebir aynı
    const [historyModal, setHistoryModal] = useState({
        visible: false, staffId: null, staffName: '',
        transactions: [], loading: false
    })

    const [faqModal, setFaqModal] = useState(false)

    // ── VERİ ÇEKME — birebir aynı ────────────────────────────────────────────
    const fetchStaff = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await usersAPI.getStaff()
            if (response.success) {
                setStaffList(response.staff || [])
                setFilteredStaff(response.staff || [])
            }
        } catch (err) {
            console.error('Personel listesi hatası:', err)
            setError('Personel listesi yüklenemedi.')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { fetchStaff() }, [fetchStaff])

    // Arama Filtresi — birebir aynı
    useEffect(() => {
        const lowerTerm = searchTerm.toLowerCase()
        const filtered = staffList.filter(staff =>
            staff.name.toLowerCase().includes(lowerTerm) ||
            (staff.surname && staff.surname.toLowerCase().includes(lowerTerm))
        )
        setFilteredStaff(filtered)
    }, [searchTerm, staffList])

    // ── openSettingsModal — birebir aynı ─────────────────────────────────────
    const openSettingsModal = async (staff) => {
        try {
            let defaultSettings = {
                base_salary: 0,
                commission_model: 'flat_rate',
                model_settings: { service_rate: 0, product_rate: 0 }
            }

            const res = await staffSalaryAPI.getSettings(staff.id)
            if (res) {
                defaultSettings = {
                    base_salary:      res.base_salary      || 0,
                    commission_model: res.commission_model || 'flat_rate',
                    model_settings:   res.model_settings   || {}
                }
            }

            setSettingsModal({
                visible:         true,
                staffId:         staff.id,
                staffName:       `${staff.name} ${staff.surname}`,
                baseSalary:      defaultSettings.base_salary,
                commissionModel: defaultSettings.commission_model,
                modelSettings:   defaultSettings.model_settings
            })
        } catch (err) {
            console.error(err)
            alert('Ayarlar yüklenirken hata oluştu.')
        }
    }

    // ── handleSaveSettings — birebir aynı ────────────────────────────────────
    const handleSaveSettings = async () => {
        setSavingSettings(true)
        try {
            const payload = {
                base_salary:      settingsModal.baseSalary,
                commission_model: settingsModal.commissionModel,
                model_settings:   settingsModal.modelSettings
            }
            await staffSalaryAPI.updateSettings(settingsModal.staffId, payload)
            setSettingsModal(prev => ({ ...prev, visible: false }))
        } catch (err) {
            alert('Ayarlar kaydedilemedi: ' + (err.response?.data?.message || err.message))
        } finally {
            setSavingSettings(false)
        }
    }

    // ── openHistoryModal — birebir aynı (tarihe göre yeniden eskiye sıralama) ─
    const openHistoryModal = async (staff) => {
        setHistoryModal({
            visible: true, staffId: staff.id,
            staffName: `${staff.name} ${staff.surname}`,
            transactions: [], loading: true
        })
        try {
            const res = await transactionsAPI.getAll({ staff_id: staff.id, type: 'expense' })
            if (res.success) {
                const sorted = res.transactions.sort((a, b) => new Date(b.date) - new Date(a.date))
                setHistoryModal(prev => ({ ...prev, transactions: sorted, loading: false }))
            }
        } catch (err) {
            console.error(err)
            setHistoryModal(prev => ({ ...prev, loading: false }))
        }
    }

    // ── openPaymentModal — birebir aynı ──────────────────────────────────────
    const openPaymentModal = (staff, type) => {
        setPaymentModal({
            visible: true,
            staffId: staff.id,
            staffName: `${staff.name} ${staff.surname}`,
            type,
            amount: '',
            note: ''
        })
    }

    // ── handlePaymentSubmit — birebir aynı ───────────────────────────────────
    const handlePaymentSubmit = async () => {
        if (!paymentModal.amount || parseFloat(paymentModal.amount) <= 0) {
            alert('Lütfen geçerli bir tutar giriniz.')
            return
        }
        setSubmittingPayment(true)
        try {
            const isSalary     = paymentModal.type === 'salary'
            const label        = isSalary ? 'Maaş Ödemesi' : 'Prim Ödemesi'
            const fullNote     = `${label} - ${paymentModal.note}`
            const categoryName = isSalary ? 'salary_expense' : 'commission_expense'

            await api.post('/admin/payments/staff', {
                staff_id:     paymentModal.staffId,
                amount:       paymentModal.amount,
                payment_type: paymentModal.type,
                category:     categoryName,
                date:         new Date().toISOString().split('T')[0],
                note:         fullNote
            })

            setPaymentModal(prev => ({ ...prev, visible: false }))
        } catch (err) {
            console.error('Ödeme hatası:', err)
            alert('Ödeme yapılamadı.')
        } finally {
            setSubmittingPayment(false)
        }
    }

    // ── Derived ───────────────────────────────────────────────────────────────
    const isSalaryPayment = paymentModal.type === 'salary'

    // ─── RENDER ───────────────────────────────────────────────────────────────
    return (
        <div className="spm-page">
            <GlobalStyles />

            {/* ── HEADER ── */}
            <div className="spm-header-card">
                <div className="spm-header-left">
                    <div className="spm-header-icon"><WalletIcon size={20} /></div>
                    <div>
                        <h1 className="spm-header-title">Maaş ve Prim Yönetimi</h1>
                        <p className="spm-header-sub">Maaş yapılandırması ve ödeme işlemleri</p>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button className="spm-help-btn" onClick={() => setFaqModal(true)}>
                        <BookIcon size={14} /> Nasıl Çalışır?
                    </button>
                    <div className="spm-search-wrap">
                        <SearchIcon />
                        <input
                            className="spm-search"
                            placeholder="Personel ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* ── İÇERİK ── */}
            {loading ? (
                <div className="spm-staff-grid">
                    {[0, 1, 2].map(i => <StaffCardSkeleton key={i} />)}
                </div>
            ) : error ? (
                <div className="spm-alert spm-alert-danger">
                    <WarnIcon /> {error}
                    <button className="spm-alert-close" onClick={() => setError(null)}><XIcon size={14} /></button>
                </div>
            ) : filteredStaff.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--spm-text3)', fontSize: '0.9rem' }}>
                    {searchTerm ? `"${searchTerm}" için sonuç bulunamadı.` : 'Henüz personel bulunmuyor.'}
                </div>
            ) : (
                <div className="spm-staff-grid">
                    {filteredStaff.map((staff, idx) => (
                        <StaffCard
                            key={staff.id}
                            staff={staff}
                            onSettings={openSettingsModal}
                            onHistory={openHistoryModal}
                            onPayment={openPaymentModal}
                            animDelay={idx * 0.06}
                        />
                    ))}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════
          MODAL: AYARLAR — birebir iş mantığı
      ═══════════════════════════════════════════════════ */}
            <Modal
                visible={settingsModal.visible}
                onClose={() => setSettingsModal(prev => ({ ...prev, visible: false }))}
                size="spm-modal-lg"
                title="Maaş ve Prim Yapılandırması"
                footer={
                    <>
                        <button className="spm-btn spm-btn-ghost" onClick={() => setSettingsModal(prev => ({ ...prev, visible: false }))}>
                            İptal
                        </button>
                        <button className="spm-btn spm-btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
                            {savingSettings ? <SpinnerIcon size={14} /> : <SaveIcon />}
                            Kaydet ve Uygula
                        </button>
                    </>
                }
            >
                {/* Info banner */}
                <div className="spm-info-banner">
                    <span className="spm-info-banner-icon"><InfoIcon size={18} /></span>
                    <div>
                        <p className="spm-info-banner-name">{settingsModal.staffName}</p>
                        <p className="spm-info-banner-sub">Personel için hesaplama modelini seçiniz.</p>
                    </div>
                </div>

                {/* Base salary */}
                <div className="spm-form-mb">
                    <label className="spm-form-label">Aylık Sabit Maaş</label>
                    <div className="spm-input-group">
                        <span className="spm-input-prefix">₺</span>
                        <input
                            type="number"
                            className="spm-input prefix-r large"
                            value={settingsModal.baseSalary}
                            onChange={(e) => setSettingsModal(prev => ({ ...prev, baseSalary: e.target.value }))}
                        />
                    </div>
                </div>

                {/* Commission model select */}
                <div className="spm-form-mb">
                    <label className="spm-form-label">Prim Hesaplama Modeli</label>
                    <select
                        className="spm-select"
                        value={settingsModal.commissionModel}
                        onChange={(e) => setSettingsModal(prev => ({ ...prev, commissionModel: e.target.value }))}
                    >
                        <option value="flat_rate">Sabit Oran (Düz Yüzde)</option>
                        <option value="threshold_complex">Eşikli Sistem (Kota + Nakit/Kart Ayrımı)</option>
                        <option value="tiered">Kademeli Sistem (Ciroya Göre Artan Oran)</option>
                    </select>
                </div>

                {/* Dynamic model inputs */}
                <ModelInputs
                    commissionModel={settingsModal.commissionModel}
                    modelSettings={settingsModal.modelSettings}
                    setSettingsModal={setSettingsModal}
                />
            </Modal>

            {/* ═══════════════════════════════════════════════════
          MODAL: GEÇMİŞ — birebir iş mantığı
          getTransactionLabel, tarihe göre sıralama, tablo
      ═══════════════════════════════════════════════════ */}
            <Modal
                visible={historyModal.visible}
                onClose={() => setHistoryModal(prev => ({ ...prev, visible: false }))}
                size="spm-modal-lg"
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryIcon size={16} /> Ödeme Geçmişi: {historyModal.staffName}
          </span>
                }
                footer={
                    <button className="spm-btn spm-btn-ghost" onClick={() => setHistoryModal(prev => ({ ...prev, visible: false }))}>
                        Kapat
                    </button>
                }
            >
                {historyModal.loading ? (
                    <div className="spm-modal-spinner">
                        <div className="spm-spinner" />
                    </div>
                ) : historyModal.transactions.length > 0 ? (
                    <div className="spm-table-scroll" style={{ padding: 0, margin: '-22px' }}>
                        <table className="spm-table">
                            <thead>
                            <tr>
                                <th>Tarih</th>
                                <th>Tür</th>
                                <th>Açıklama</th>
                                <th style={{ textAlign: 'right' }}>Tutar</th>
                            </tr>
                            </thead>
                            <tbody>
                            {historyModal.transactions.map((tx) => {
                                const label = getTransactionLabel(tx)
                                const badgeStyle = TX_BADGE_STYLES[label.color] || TX_BADGE_STYLES.neutral
                                return (
                                    <tr key={tx.id}>
                                        <td className="spm-table-date">{formatDate(tx.date)}</td>
                                        <td>
                        <span
                            className="spm-tx-badge"
                            style={{ background: badgeStyle.bg, color: badgeStyle.color }}
                        >
                          {label.text}
                        </span>
                                        </td>
                                        <td style={{ fontSize: '0.8rem', color: 'var(--spm-text2)' }}>
                                            {tx.description || tx.note || '-'}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <span className="spm-table-amount">-{formatCurrency(tx.amount)}</span>
                                        </td>
                                    </tr>
                                )
                            })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="spm-history-empty">Henüz kayıtlı bir ödeme bulunamadı.</div>
                )}
            </Modal>

            {/* ═══════════════════════════════════════════════════
          MODAL: ÖDEME YAP — birebir iş mantığı
          salary_expense / commission_expense kategori
      ═══════════════════════════════════════════════════ */}
            <Modal
                visible={paymentModal.visible}
                onClose={() => setPaymentModal(prev => ({ ...prev, visible: false }))}
                title={
                    <span style={{ color: isSalaryPayment ? 'var(--spm-info)' : 'var(--spm-warn)', display: 'flex', alignItems: 'center', gap: 8 }}>
            {isSalaryPayment ? <MoneyIcon size={16} /> : <CreditCardIcon size={16} />}
                        {isSalaryPayment ? 'Maaş Ödemesi' : 'Prim Ödemesi'}
          </span>
                }
                footer={
                    <>
                        <button className="spm-btn spm-btn-ghost" onClick={() => setPaymentModal(prev => ({ ...prev, visible: false }))}>
                            İptal
                        </button>
                        <button
                            className={`spm-btn ${isSalaryPayment ? 'spm-btn-info' : 'spm-btn-warn'}`}
                            onClick={handlePaymentSubmit}
                            disabled={submittingPayment}
                        >
                            {submittingPayment ? <SpinnerIcon size={14} /> : <ArrowRightIcon />}
                            Ödemeyi Onayla
                        </button>
                    </>
                }
            >
                <p style={{ textAlign: 'center', fontWeight: 800, fontSize: '1rem', color: 'var(--spm-text1)', margin: '0 0 16px' }}>
                    {paymentModal.staffName}
                </p>

                <div className="spm-form-mb">
                    <label className="spm-form-label">Tutar</label>
                    <div className="spm-input-group">
                        <span className="spm-input-prefix">₺</span>
                        <input
                            type="number"
                            className="spm-input prefix-r large"
                            placeholder="0.00"
                            value={paymentModal.amount}
                            onChange={(e) => setPaymentModal(prev => ({ ...prev, amount: e.target.value }))}
                            autoFocus
                        />
                    </div>
                </div>

                <div>
                    <label className="spm-form-label">Açıklama</label>
                    <textarea
                        className="spm-textarea"
                        rows={2}
                        placeholder="Örn: Ekim ayı hakediş..."
                        value={paymentModal.note}
                        onChange={(e) => setPaymentModal(prev => ({ ...prev, note: e.target.value }))}
                    />
                </div>
            </Modal>

            {/* ═══════════════════════════════════════════════════
          MODAL: EĞİTİM / SSS — birebir aynı içerik
      ═══════════════════════════════════════════════════ */}
            <Modal
                visible={faqModal}
                onClose={() => setFaqModal(false)}
                size="spm-modal-lg"
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LightbulbIcon size={16} style={{ color: 'var(--spm-warn)' }} /> Sistem Nasıl Çalışır?
          </span>
                }
                footer={
                    <button className="spm-btn spm-btn-primary" onClick={() => setFaqModal(false)}>Anlaşıldı</button>
                }
            >
                <div className="spm-accordion">
                    <AccordionItem title="1. Sabit Oran (Flat Rate) Nedir?" defaultOpen>
                        Bu en basit modeldir. Personelin yaptığı her satıştan belirli bir yüzde alır.<br />
                        Örneğin: Hizmet Primi %10 ise, 1000 TL'lik saç kesiminden 100 TL prim alır.<br />
                        Ciro limiti veya ödeme yöntemi (nakit/kart) fark etmeksizin çalışır.
                    </AccordionItem>
                    <AccordionItem title="2. Eşikli Sistem (Threshold) Nedir?">
                        Bu modelde personele bir <strong>Kota (Eşik)</strong> belirlersiniz (Örn: 10.000 TL).<br />
                        Personel bu kotayı geçmeden prim alamaz.<br /><br />
                        <strong>Detaylar:</strong>
                        <ul>
                            <li><strong>Nakit/Kart Ayrımı:</strong> Nakit ödemeler için ayrı, kredi kartı için ayrı prim yüzdesi belirleyebilirsiniz.</li>
                            <li><strong>Kota Dahil/Hariç:</strong> Prim hesaplanırken ilk 10.000 TL'nin dahil edilip edilmeyeceğini seçebilirsiniz.</li>
                        </ul>
                    </AccordionItem>
                    <AccordionItem title="3. Kademeli Sistem (Tiered) Nedir?">
                        Personelin cirosu arttıkça prim oranı da artar.<br />
                        Örnek:<br />
                        - 0 TL - 20.000 TL arası ciroya → <strong>%5</strong><br />
                        - 20.000 TL ve üzeri ciroya → <strong>%10</strong><br />
                        Bu sistem personeli daha çok satış yapmaya teşvik eder.
                    </AccordionItem>
                    <AccordionItem title="Ciro Nasıl Hesaplanır?">
                        Ciro hesaplanırken <strong>sadece o personelin</strong> işlem yaptığı adisyonlar baz alınır.
                        Başka bir personelin yaptığı işlemler hesaba katılmaz.
                    </AccordionItem>
                </div>
            </Modal>
        </div>
    )
}

export default AdminSalaryPrimManagement